import { createContext, useContext } from 'react';
import { StyleSheet } from 'react-native';
import type { ChatSendAttempt } from '../../services/chat/sendAttempt';

/**
 * CORE-1791 — признак «этот тред живёт в разделе сообщений (/chats)».
 *
 * Тред (`AgapellaChatThread`), палитра (`EmojiStrip`) и панели общие для шести
 * мест: виджет Агапеллы, круги, обсуждения мастера, гостевой чат главной, Live.
 * Редизайн раздела сообщений не должен перекрасить и переложить их, поэтому
 * всё новое включается только внутри этого контекста, а его даёт один экран —
 * `ChatsScreen`. Пропсом через панели не развести: `ChatApiCompanionPanel` один
 * и тот же и в /chats, и в виджете.
 *
 * Без провайдера `useMessagesSurface()` возвращает `null`, и каждый потребитель
 * обязан вести себя ровно как до CORE-1791 (это проверяет тест развязки).
 */
export interface MessagesSurface {
  /** Раскладка телефона: список и диалог — отдельные экраны (≤700 px, телефон боком). */
  phone: boolean;
  /** Раскладка раздела: геометрия материала v3 (wide / сжатие 1100 / телефон). */
  layout: 'phone' | 'compact' | 'wide';
  /**
   * Диалог сейчас на экране. На телефоне, пока показан список, тред не
   * размонтируется (черновик, прокрутка), но прочитанным ничего не отмечает.
   */
  threadShown: boolean;
  /** Ключ черновика открытого диалога (`entityType:id`); `null` — диалог не выбран. */
  draftKey: string | null;
  /** Хранилище черновиков дочитано — до этого пустое поле не значит «черновика нет». */
  draftsReady: boolean;
  getDraft: (key: string) => string;
  setDraft: (key: string, text: string) => void;
  /**
   * CORE-1791 (возврат QA 28.10) — отправка диалога, текст которой вернулся в
   * черновик (ключ повтора и текст). Без них композер помнит её только до
   * перехода в другой диалог.
   */
  /**
   * CORE-2107 — это очередь: все неподтверждённые отправки диалога по порядку.
   * `setSendAttempt` заменяет запись с тем же ключом или добавляет в конец,
   * `dropSendAttempt` убирает одну.
   */
  getSendAttempts?: (key: string) => ChatSendAttempt[];
  setSendAttempt?: (key: string, attempt: ChatSendAttempt) => void;
  dropSendAttempt?: (key: string, clientMessageId: string) => void;
}

export const MessagesSurfaceContext = createContext<MessagesSurface | null>(null);

export function useMessagesSurface(): MessagesSurface | null {
  return useContext(MessagesSurfaceContext);
}

/**
 * Минимальная область нажатия в разделе сообщений (ТЗ CORE-1791, WCAG 2.5.5).
 * На вебе `hitSlop` не работает — react-native-web его не реализует, поэтому
 * область задаётся размером самого нажимаемого элемента, а видимая часть
 * остаётся прежней внутри него.
 */
export const MESSAGES_HIT = 44;

/** Квадрат области нажатия с иконкой по центру. */
export const hitBox = { width: MESSAGES_HIT, height: MESSAGES_HIT, alignItems: 'center', justifyContent: 'center' } as const;

/**
 * Область 44 вокруг видимого элемента размера `visual`, не сдвигающая соседей:
 * лишнее по краям гасится отрицательными полями.
 */
export function hitAround(visualW: number, visualH: number = visualW) {
  const dx = Math.max(0, (MESSAGES_HIT - visualW) / 2);
  const dy = Math.max(0, (MESSAGES_HIT - visualH) / 2);
  return {
    minWidth: MESSAGES_HIT,
    minHeight: MESSAGES_HIT,
    marginHorizontal: -dx,
    marginVertical: -dy,
    alignItems: 'center',
    justifyContent: 'center',
  } as const;
}

/**
 * CORE-1791 — области нажатия раздела сообщений. Только размеры и поля:
 * видимое оформление контролов сюда не входит (оно придёт с макетом v3).
 * Отрицательные поля гасят прирост, чтобы соседи не сдвигались.
 */
export const makeHitStyles = () => StyleSheet.create({
  // Кнопки поля и полосы записи: скрепка 40, эмодзи 36 — теперь 44.
  square: { ...hitBox },
  // Круглые отправка / микрофон / «остановить»: видимые 40 (на телефоне 46) —
  // внутри области не меньше 44, бо́льшая видимая часть области не обрезается.
  round: { minWidth: MESSAGES_HIT, minHeight: MESSAGES_HIT, alignItems: 'center', justifyContent: 'center' },
  // «Назад» в шапке диалога: стрелка остаётся на прежнем месте, шапка не растёт.
  backHeaderBtn: { ...hitBox, padding: 0, marginLeft: -10, marginVertical: -3 },
  // «…» у пузыря (видимая часть 24×21) — область 44 без сдвига пузыря.
  menuBtn: { ...hitAround(24, 21), paddingHorizontal: 0, paddingVertical: 0, marginBottom: 2 - (MESSAGES_HIT - 21) / 2 },
  // Крестик отмены ответа/правки (глиф 18).
  bannerClose: { ...hitAround(18) },
  // Строки меню сообщения, подсказки упоминаний, закреплённое.
  menuItem: { minHeight: MESSAGES_HIT },
  minRow: { minHeight: MESSAGES_HIT },
  // Выбор реакции в меню: ячейки 44, ряд переносится, а не уезжает вбок.
  picker: { flexWrap: 'wrap', gap: 0 },
  pickerItem: { width: MESSAGES_HIT, height: MESSAGES_HIT, paddingHorizontal: 0 },
  // Чип реакции под сообщением (видимая высота ~26): область 44, ряд не растёт.
  reactionChip: { ...hitAround(26, 26), marginHorizontal: 0 },
  // Быстрый ответ Агапеллы (видимая высота ~32).
  chipHit: { minHeight: MESSAGES_HIT, marginVertical: -6, justifyContent: 'center' },
  // Крестик файла в очереди: центр области — центр прежнего кружка.
  stagedRemoveHit: { position: 'absolute', top: 3 - MESSAGES_HIT / 2, right: 3 - MESSAGES_HIT / 2, ...hitBox },
  stagedRemoveHitPhone: { top: 15 - MESSAGES_HIT / 2, right: 15 - MESSAGES_HIT / 2 },
  stagedRemoveVisual: { position: 'relative', top: 0, right: 0 },
  viewerClose: { width: MESSAGES_HIT, height: MESSAGES_HIT, borderRadius: MESSAGES_HIT / 2 },
});

export type HitStyles = ReturnType<typeof makeHitStyles>;

/**
 * CORE-1791 — раскладка палитры эмодзи. В разделе сообщений каждая картинка —
 * кнопка 44×44, а ряд переносится: на телефоне одиннадцать кнопок по 44 шире
 * экрана, и прежний горизонтальный скролл прятал половину комплекта. Вне
 * раздела — прежний ряд со скроллом и прежние размеры.
 */
export function emojiStripLayout(surface: MessagesSurface | null): { wrap: boolean; cell: number | null } {
  return surface ? { wrap: true, cell: MESSAGES_HIT } : { wrap: false, cell: null };
}
